/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react'
import Cookies from 'js-cookie'
import { Outlet } from '@tanstack/react-router'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { useGetModule } from '@/services/modulesService'
import { useAuth } from '@/stores/authStore'
import { useModulesStore } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import { HeroNavbarProvider } from '@/context/hero-navbar-context'
import { SearchProvider } from '@/context/search-context'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/layout/app-sidebar'
import SkipToMain from '@/components/skip-to-main'

interface Props {
  children?: React.ReactNode
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { accessToken } = useAuth()

  useEffect(() => {
    if (!accessToken) {
      navigate({
        to: '/sign-in',
        search: {
          redirect: '/question-lib',
        },
        replace: true,
      })
    }
  }, [accessToken, navigate, location])

  if (!accessToken) {
    return null
  }

  return <>{children}</>
}

export function AuthenticatedLayout({ children }: Props) {
  const { modules } = useGetModule()
  const { setItems: setModules } = useModulesStore()

  useEffect(() => {
    if (modules && modules?.length > 0) {
      setModules(modules)
    }
  }, [modules])

  const defaultOpen = Cookies.get('sidebar_state') !== 'false'
  return (
    <AuthGuard>
      <SearchProvider>
        <HeroNavbarProvider>
          <SidebarProvider defaultOpen={defaultOpen}>
            <SkipToMain />
            <AppSidebar />
            <div
              id='content'
              className={cn(
                'ml-auto w-full max-w-full bg-[#f2f4f7]',
                'peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]',
                'peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]',
                'flex min-h-svh flex-col'
              )}
            >
              {children ? children : <Outlet />}
            </div>
          </SidebarProvider>
        </HeroNavbarProvider>
      </SearchProvider>
    </AuthGuard>
  )
}
